import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Clock, User, GripVertical, Move, Link2, ZoomIn, ZoomOut, CalendarDays, ChevronLeft, ChevronRight, AlertTriangle, Maximize2, Minimize2, X, Plus } from 'lucide-react';
import { toast } from 'sonner';

const ZOOM_LEVELS = {
  days: { label: 'Tage', daysVisible: 14 },
  weeks: { label: 'Wochen', daysVisible: 42 },
  months: { label: 'Monate', daysVisible: 90 },
  quarters: { label: 'Quartale', daysVisible: 180 },
  all: { label: 'Alles', daysVisible: null }
};

const TimelineComponent = ({ project, tasks, onTaskUpdate, onTaskReorder, onTaskDependencyUpdate, onCreateTask, isCompleted = false, criticalPathTaskIds = [], showCriticalPath = false }) => {
  const [draggingTask, setDraggingTask] = useState(null);
  const [dragType, setDragType] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [linkingFrom, setLinkingFrom] = useState(null);
  const [zoomLevel, setZoomLevel] = useState('all');
  const [viewStartOffset, setViewStartOffset] = useState(0); // Days offset from project start
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [renderKey, setRenderKey] = useState(0); // Force re-render
  const [dragDatePreview, setDragDatePreview] = useState(null); // NEW: Date preview during drag
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 }); // NEW: Mouse position during drag
  const [viewMode, setViewMode] = useState('start'); // NEW: 'start' = from project start, 'today' = from today
  const [isDraggingScrollbar, setIsDraggingScrollbar] = useState(false); // Scrollbar drag state
  const [dragStartOffset, setDragStartOffset] = useState(0); // Offset from task start when drag began (in percent)
  const [timelineWidth, setTimelineWidth] = useState(500); // Track timeline width in state
  const timelineRef = useRef(null);
  const taskRowsRef = useRef({});
  const containerRef = useRef(null);
  const scrollbarRef = useRef(null);

  // Handle ESC key to exit fullscreen
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);

  // Prevent body scroll when fullscreen
  useEffect(() => {
    if (isFullscreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isFullscreen]);

  // Reset viewStartOffset when viewMode changes
  useEffect(() => {
    setViewStartOffset(0);
  }, [viewMode]);

  // Force re-render after fullscreen change to recalculate arrow positions
  useEffect(() => {
    const timer = setTimeout(() => {
      setRenderKey(prev => prev + 1);
    }, 50);
    return () => clearTimeout(timer);
  }, [isFullscreen]);

  // Set of critical path task IDs for quick lookup - only use when showCriticalPath is true
  const criticalTaskSet = useMemo(() => {
    if (!showCriticalPath) return new Set();
    return new Set(criticalPathTaskIds);
  }, [criticalPathTaskIds, showCriticalPath]);

  // Calculate the full project bounds (including all tasks)
  const fullBounds = useMemo(() => {
    // Safety check for project dates
    if (!project?.start_date || !project?.end_date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const defaultEnd = new Date(today);
      defaultEnd.setDate(defaultEnd.getDate() + 30);
      return {
        minDate: today.getTime(),
        maxDate: defaultEnd.getTime(),
        todayPosition: 0
      };
    }
    
    const projectStart = new Date(project.start_date).getTime();
    const projectEnd = new Date(project.end_date).getTime();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayTime = today.getTime();
    
    let minDate = projectStart;
    let maxDate = projectEnd;
    
    tasks.forEach(task => {
      if (task.start_date) {
        minDate = Math.min(minDate, new Date(task.start_date).getTime());
      }
      if (task.due_date) {
        maxDate = Math.max(maxDate, new Date(task.due_date).getTime());
      }
    });
    
    // Add padding
    const padding = 3 * 24 * 60 * 60 * 1000; // 3 days
    
    // If viewMode is 'today', start from today instead of project start
    const effectiveStart = viewMode === 'today' 
      ? todayTime - (1 * 24 * 60 * 60 * 1000) // 1 day before today for a bit of context
      : minDate - padding;
    
    return {
      start: effectiveStart,
      end: maxDate + padding,
      totalDays: Math.ceil((maxDate + padding - effectiveStart) / (1000 * 60 * 60 * 24)),
      originalStart: minDate - padding // Keep original for reference
    };
  }, [project, tasks, viewMode]);

  // Calculate visible bounds based on zoom level
  const viewBounds = useMemo(() => {
    const zoomConfig = ZOOM_LEVELS[zoomLevel];
    
    if (zoomLevel === 'all' || !zoomConfig.daysVisible) {
      return fullBounds;
    }
    
    // In fullscreen mode, show more days (approx 2x) to utilize the extra space
    const fullscreenMultiplier = isFullscreen ? 2 : 1;
    const daysVisible = zoomConfig.daysVisible * fullscreenMultiplier;
    const msPerDay = 24 * 60 * 60 * 1000;
    
    // Calculate view start based on offset
    const viewStart = fullBounds.start + (viewStartOffset * msPerDay);
    const viewEnd = viewStart + (daysVisible * msPerDay);
    
    return {
      start: viewStart,
      end: Math.min(viewEnd, fullBounds.end + (7 * msPerDay)),
      totalDays: daysVisible
    };
  }, [zoomLevel, viewStartOffset, fullBounds, isFullscreen]);

  // Navigation functions
  const canScrollLeft = viewStartOffset > 0;
  const canScrollRight = zoomLevel !== 'all' && 
    (viewBounds.end < fullBounds.end + (7 * 24 * 60 * 60 * 1000));

  const scrollLeft = () => {
    const fullscreenMultiplier = isFullscreen ? 2 : 1;
    const scrollAmount = Math.floor(ZOOM_LEVELS[zoomLevel].daysVisible * fullscreenMultiplier / 3);
    setViewStartOffset(Math.max(0, viewStartOffset - scrollAmount));
  };

  const scrollRight = () => {
    const fullscreenMultiplier = isFullscreen ? 2 : 1;
    const scrollAmount = Math.floor(ZOOM_LEVELS[zoomLevel].daysVisible * fullscreenMultiplier / 3);
    setViewStartOffset(viewStartOffset + scrollAmount);
  };

  const getTimelinePosition = useCallback((date) => {
    if (!project) return 0;
    
    const taskDate = new Date(date).getTime();
    const position = ((taskDate - viewBounds.start) / (viewBounds.end - viewBounds.start)) * 100;
    return position;
  }, [project, viewBounds]);

  const positionToDate = useCallback((positionPercent) => {
    const clampedPosition = Math.max(0, Math.min(100, positionPercent));
    const timestamp = viewBounds.start + (clampedPosition / 100) * (viewBounds.end - viewBounds.start);
    return new Date(timestamp);
  }, [viewBounds]);

  // Get timeline markers based on zoom level
  const getTimelineMarkers = useCallback(() => {
    if (!project) return [];
    
    const start = new Date(viewBounds.start);
    const end = new Date(viewBounds.end);
    const markers = [];
    
    switch (zoomLevel) {
      case 'days': {
        // Show individual days, but only every 2nd or 3rd day to avoid overlap
        let current = new Date(start);
        current.setHours(0, 0, 0, 0);
        let dayCount = 0;
        // In fullscreen, we show more days so we need to show fewer labels
        const showEveryNthDay = isFullscreen ? 3 : 2;
        const maxDaysForAllLabels = isFullscreen ? 21 : 14;
        while (current <= end) {
          const position = getTimelinePosition(current.toISOString());
          if (position >= -5 && position <= 105) {
            const dayOfWeek = current.getDay();
            const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
            // Show every day but with short labels - adjust for fullscreen
            const totalDays = viewBounds.totalDays || 14;
            if (dayCount % showEveryNthDay === 0 || totalDays <= maxDaysForAllLabels) {
              markers.push({ 
                date: new Date(current), 
                label: current.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit' }),
                position,
                isWeekend,
                type: 'day'
              });
            }
          }
          current.setDate(current.getDate() + 1);
          dayCount++;
        }
        break;
      }
        
      case 'weeks': {
        let current = new Date(start);
        current.setDate(current.getDate() - current.getDay() + 1);
        while (current <= end) {
          const position = getTimelinePosition(current.toISOString());
          if (position >= -5 && position <= 105) {
            const weekStart = new Date(current);
            markers.push({ 
              date: weekStart, 
              label: `${weekStart.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit' })}`,
              position,
              type: 'week'
            });
          }
          current.setDate(current.getDate() + 7);
        }
        break;
      }
        
      case 'months': {
        let current = new Date(start);
        current.setDate(1);
        while (current <= end) {
          const position = getTimelinePosition(current.toISOString());
          if (position >= -5 && position <= 105) {
            markers.push({ 
              date: new Date(current), 
              label: current.toLocaleDateString('de-CH', { month: 'short', year: '2-digit' }), 
              position,
              type: 'month'
            });
          }
          current.setMonth(current.getMonth() + 1);
        }
        break;
      }
        
      case 'quarters':
      case 'all':
      default: {
        const totalDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
        
        if (totalDays <= 90) {
          // Show months
          let current = new Date(start);
          current.setDate(1);
          while (current <= end) {
            const position = getTimelinePosition(current.toISOString());
            if (position >= -5 && position <= 105) {
              markers.push({ 
                date: new Date(current), 
                label: current.toLocaleDateString('de-CH', { month: 'short', year: '2-digit' }), 
                position,
                type: 'month'
              });
            }
            current.setMonth(current.getMonth() + 1);
          }
        } else {
          // Show quarters
          let current = new Date(start);
          current.setDate(1);
          current.setMonth(Math.floor(current.getMonth() / 3) * 3);
          while (current <= end) {
            const position = getTimelinePosition(current.toISOString());
            if (position >= -5 && position <= 105) {
              const quarter = Math.floor(current.getMonth() / 3) + 1;
              markers.push({ 
                date: new Date(current), 
                label: `Q${quarter} ${current.getFullYear()}`, 
                position,
                type: 'quarter'
              });
            }
            current.setMonth(current.getMonth() + 3);
          }
        }
        break;
      }
    }
    
    return markers;
  }, [project, viewBounds, zoomLevel, getTimelinePosition]);

  const calculateProgress = () => {
    if (tasks.length === 0) return 0;
    const completedTasks = tasks.filter(t => t.completed).length;
    return Math.round((completedTasks / tasks.length) * 100);
  };

  // Helper functions
  const getDependentTasks = useCallback((taskId) => {
    return tasks.filter(t => t.depends_on && t.depends_on.includes(taskId));
  }, [tasks]);

  const calculateCascadingUpdates = useCallback((movedTaskId, newEndDate) => {
    const updates = [];
    const newEndTime = new Date(newEndDate).getTime();
    
    const processTask = (taskId, predecessorEndTime) => {
      const dependentTasks = getDependentTasks(taskId);
      
      dependentTasks.forEach(depTask => {
        const depStartTime = depTask.start_date ? new Date(depTask.start_date).getTime() : new Date(depTask.due_date).getTime();
        
        if (depStartTime < predecessorEndTime) {
          const duration = depTask.start_date 
            ? new Date(depTask.due_date).getTime() - new Date(depTask.start_date).getTime()
            : 0;
          
          const newStart = new Date(predecessorEndTime + 24 * 60 * 60 * 1000);
          const newStartStr = newStart.toISOString().split('T')[0];
          const newEndStr = duration > 0 
            ? new Date(newStart.getTime() + duration).toISOString().split('T')[0]
            : newStartStr;
          
          updates.push({
            id: depTask.id,
            title: depTask.title,
            start_date: depTask.start_date ? newStartStr : null,
            due_date: newEndStr
          });
          
          processTask(depTask.id, new Date(newEndStr).getTime());
        }
      });
    };
    
    processTask(movedTaskId, newEndTime);
    return updates;
  }, [getDependentTasks]);

  // Zoom handlers
  const handleZoomChange = (newLevel) => {
    setZoomLevel(newLevel);
    // Calculate offset to align project start at left edge
    if (newLevel !== 'all' && project?.start_date) {
      const projectStart = new Date(project.start_date).getTime();
      const msPerDay = 24 * 60 * 60 * 1000;
      const offsetDays = Math.max(0, Math.floor((projectStart - fullBounds.start) / msPerDay) - 1); // -1 for small padding
      setViewStartOffset(offsetDays);
    } else {
      setViewStartOffset(0);
    }
  };

  // Drag handlers
  const handleReorderDragStart = (e, task, index) => {
    if (isCompleted) return;
    setDraggingTask(task);
    setDragType('reorder');
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleTimelineDragStart = (e, task) => {
    if (isCompleted) return;
    
    // Calculate offset: how far from the task's START the user clicked
    const taskBar = e.target.closest('[data-task-bar]');
    if (taskBar) {
      const barRect = taskBar.getBoundingClientRect();
      const clickX = e.clientX - barRect.left;
      const barWidth = barRect.width;
      // Store offset as percentage of the task bar width
      const offsetPercent = barWidth > 0 ? (clickX / barWidth) * 100 : 0;
      setDragStartOffset(offsetPercent);
    } else {
      setDragStartOffset(0);
    }
    
    setDraggingTask(task);
    setDragType('timeline');
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDraggingTask(null);
    setDragType(null);
    setDragOverIndex(null);
    setDragDatePreview(null);
    setDragPosition({ x: 0, y: 0 });
    setDragStartOffset(0);
  };

  // NEW: Calculate and show date preview during drag
  const handleTimelineDragOver = useCallback((e, timelineElement, taskId) => {
    if (!draggingTask || dragType !== 'timeline' || isCompleted) return;
    e.preventDefault();
    
    const rect = timelineElement.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const mousePositionPercent = Math.max(0, Math.min(100, (x / rect.width) * 100));
    
    // Calculate where the TASK START should be, not the mouse position
    // We need to subtract the offset (how far into the task the user clicked)
    let taskStartPositionPercent = mousePositionPercent;
    
    if (draggingTask.start_date && draggingTask.due_date) {
      // Get the task's visual width in percent
      const taskStartPercent = getTimelinePosition(draggingTask.start_date);
      const taskEndPercent = getTimelinePosition(draggingTask.due_date);
      const taskWidthPercent = Math.max(0, taskEndPercent - taskStartPercent);
      
      // Calculate where in the task the user clicked (dragStartOffset is 0-100% of task width)
      const offsetInTimelinePercent = (dragStartOffset / 100) * taskWidthPercent;
      
      // Subtract the offset so the preview shows where the START of the task will be
      taskStartPositionPercent = Math.max(0, Math.min(100, mousePositionPercent - offsetInTimelinePercent));
    }
    
    const rawDate = positionToDate(taskStartPositionPercent);
    // Normalize to start of day (midnight) so the preview line aligns with day grid
    const newDate = new Date(rawDate);
    newDate.setHours(0, 0, 0, 0);
    
    // Calculate new start and end dates based on task duration
    let previewStart = newDate;
    let previewEnd = newDate;
    
    if (draggingTask.start_date && draggingTask.due_date) {
      const originalStart = new Date(draggingTask.start_date);
      const originalEnd = new Date(draggingTask.due_date);
      // Calculate duration in days (normalized)
      const durationDays = Math.round((originalEnd - originalStart) / (1000 * 60 * 60 * 24));
      
      previewStart = newDate;
      previewEnd = new Date(newDate);
      previewEnd.setDate(previewEnd.getDate() + durationDays);
    }
    
    setDragDatePreview({
      start: previewStart,
      end: previewEnd,
      targetTaskId: taskId
    });
    
    setDragPosition({ x: e.clientX, y: e.clientY });
  }, [draggingTask, dragType, isCompleted, positionToDate, dragStartOffset, getTimelinePosition]);

  const handleReorderDragOver = (e, index) => {
    if (!draggingTask || dragType !== 'reorder' || isCompleted) return;
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleReorderDrop = async (e, targetIndex) => {
    if (!draggingTask || dragType !== 'reorder' || !onTaskReorder || isCompleted) return;
    e.preventDefault();
    
    const sourceIndex = tasks.findIndex(t => t.id === draggingTask.id);
    if (sourceIndex === targetIndex) {
      handleDragEnd();
      return;
    }
    
    const newTasks = [...tasks];
    const [movedTask] = newTasks.splice(sourceIndex, 1);
    newTasks.splice(targetIndex, 0, movedTask);
    
    const taskOrders = newTasks.map((task, idx) => ({ id: task.id, order: idx }));
    
    try {
      await onTaskReorder(taskOrders);
      toast.success(`"${draggingTask.title}" neu angeordnet`);
    } catch (error) {
      toast.error('Fehler beim Neuordnen');
    }
    
    handleDragEnd();
  };

  const handleTimelineDrop = async (e, timelineElement) => {
    if (!draggingTask || dragType !== 'timeline' || !onTaskUpdate || isCompleted) return;
    e.preventDefault();
    
    const rect = timelineElement.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const mousePositionPercent = Math.max(0, Math.min(100, (x / rect.width) * 100));
    
    // Calculate where the TASK START should be (same logic as dragOver)
    let taskStartPositionPercent = mousePositionPercent;
    
    if (draggingTask.start_date && draggingTask.due_date) {
      const taskStartPercent = getTimelinePosition(draggingTask.start_date);
      const taskEndPercent = getTimelinePosition(draggingTask.due_date);
      const taskWidthPercent = Math.max(0, taskEndPercent - taskStartPercent);
      const offsetInTimelinePercent = (dragStartOffset / 100) * taskWidthPercent;
      taskStartPositionPercent = Math.max(0, Math.min(100, mousePositionPercent - offsetInTimelinePercent));
    }
    
    const rawDate = positionToDate(taskStartPositionPercent);
    // Normalize to start of day
    const newDate = new Date(rawDate);
    newDate.setHours(0, 0, 0, 0);
    const formattedDate = newDate.toISOString().split('T')[0];
    
    let newStartDate = null;
    let newDueDate = formattedDate;
    
    if (draggingTask.start_date) {
      const originalStart = new Date(draggingTask.start_date);
      const originalEnd = new Date(draggingTask.due_date);
      const durationDays = Math.round((originalEnd - originalStart) / (1000 * 60 * 60 * 24));
      
      newStartDate = formattedDate;
      const endDate = new Date(newDate);
      endDate.setDate(endDate.getDate() + durationDays);
      newDueDate = endDate.toISOString().split('T')[0];
    }
    
    // Check predecessor constraints
    if (draggingTask.depends_on && draggingTask.depends_on.length > 0) {
      const taskMap = Object.fromEntries(tasks.map(t => [t.id, t]));
      for (const predId of draggingTask.depends_on) {
        const pred = taskMap[predId];
        if (pred) {
          const predEndTime = new Date(pred.due_date).getTime();
          const newStartTime = newStartDate ? new Date(newStartDate).getTime() : new Date(newDueDate).getTime();
          
          if (newStartTime <= predEndTime) {
            toast.error(`Task kann nicht vor "${pred.title}" beginnen`);
            handleDragEnd();
            return;
          }
        }
      }
    }
    
    try {
      await onTaskUpdate(draggingTask.id, { start_date: newStartDate, due_date: newDueDate });
      
      const cascadingUpdates = calculateCascadingUpdates(draggingTask.id, newDueDate);
      
      if (cascadingUpdates.length > 0) {
        for (const update of cascadingUpdates) {
          await onTaskUpdate(update.id, { start_date: update.start_date, due_date: update.due_date });
        }
        toast.success(`"${draggingTask.title}" + ${cascadingUpdates.length} Task(s) angepasst`);
      } else {
        toast.success(`"${draggingTask.title}" verschoben`);
      }
    } catch (error) {
      toast.error('Fehler beim Verschieben');
    }
    
    handleDragEnd();
  };

  // Dependency linking
  const handleStartLinking = (task) => {
    if (isCompleted || !onTaskDependencyUpdate) return;
    setLinkingFrom(task);
    toast.info(`Klicken Sie auf den abhängigen Task`, { duration: 3000 });
  };

  const handleLinkTo = async (targetTask) => {
    if (!linkingFrom || !onTaskDependencyUpdate || isCompleted) return;
    
    if (linkingFrom.id === targetTask.id) {
      toast.error('Task kann nicht von sich selbst abhängen');
      setLinkingFrom(null);
      return;
    }
    
    if (targetTask.depends_on?.includes(linkingFrom.id)) {
      toast.error('Abhängigkeit existiert bereits');
      setLinkingFrom(null);
      return;
    }
    
    if (linkingFrom.depends_on?.includes(targetTask.id)) {
      toast.error('Zirkuläre Abhängigkeit nicht erlaubt');
      setLinkingFrom(null);
      return;
    }
    
    const newDependsOn = [...(targetTask.depends_on || []), linkingFrom.id];
    
    const targetStartTime = targetTask.start_date 
      ? new Date(targetTask.start_date).getTime() 
      : new Date(targetTask.due_date).getTime();
    const predecessorEndTime = new Date(linkingFrom.due_date).getTime();
    
    try {
      if (targetStartTime <= predecessorEndTime) {
        const duration = targetTask.start_date 
          ? new Date(targetTask.due_date).getTime() - new Date(targetTask.start_date).getTime()
          : 0;
        
        const newStart = new Date(predecessorEndTime + 24 * 60 * 60 * 1000);
        const newStartStr = newStart.toISOString().split('T')[0];
        const newEndStr = duration > 0 
          ? new Date(newStart.getTime() + duration).toISOString().split('T')[0]
          : newStartStr;
        
        await onTaskDependencyUpdate(targetTask.id, newDependsOn);
        await onTaskUpdate(targetTask.id, { 
          start_date: targetTask.start_date ? newStartStr : null, 
          due_date: newEndStr 
        });
        
        toast.success(`Abhängigkeit erstellt und Task verschoben`);
      } else {
        await onTaskDependencyUpdate(targetTask.id, newDependsOn);
        toast.success(`Abhängigkeit erstellt`);
      }
    } catch (error) {
      toast.error('Fehler beim Erstellen der Abhängigkeit');
    }
    
    setLinkingFrom(null);
  };

  const handleRemoveDependency = async (task, dependencyId) => {
    if (!onTaskDependencyUpdate || isCompleted) return;
    
    const newDependsOn = (task.depends_on || []).filter(id => id !== dependencyId);
    
    try {
      await onTaskDependencyUpdate(task.id, newDependsOn);
      toast.success('Abhängigkeit entfernt');
    } catch (error) {
      toast.error('Fehler');
    }
  };

  // Sort tasks by order
  const sortedTasks = [...tasks].sort((a, b) => (a.order || 0) - (b.order || 0));
  const taskMap = Object.fromEntries(tasks.map(t => [t.id, t]));

  // Dynamic sizing based on fullscreen mode
  const ROW_HEIGHT = isFullscreen ? 44 : 56;
  const HEADER_HEIGHT = isFullscreen ? 52 : 64;
  const LEFT_COLUMN_WIDTH = isFullscreen ? 180 : 220;

  // Track timeline width changes (must be after LEFT_COLUMN_WIDTH is defined)
  useEffect(() => {
    const updateWidth = () => {
      if (timelineRef.current) {
        const columnWidth = isFullscreen ? 180 : 220;
        setTimelineWidth(timelineRef.current.clientWidth - columnWidth || 500);
      }
    };
    
    // Initial measurement after render
    const timeoutId = setTimeout(updateWidth, 0);
    window.addEventListener('resize', updateWidth);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateWidth);
    };
  }, [isFullscreen, renderKey]);

  return (
    <>
      {/* Fullscreen Backdrop */}
      {isFullscreen && (
        <div 
          className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm" 
          onClick={() => setIsFullscreen(false)}
        />
      )}
      
      {/* Timeline Card */}
      <Card 
        data-testid="project-timeline-card" 
        className={isFullscreen ? 'fixed inset-2 z-50 overflow-auto shadow-2xl flex flex-col' : ''}
        onClick={(e) => e.stopPropagation()}
      >
      <CardHeader className={isFullscreen ? "pb-2 flex-shrink-0" : "pb-3"}>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className={`flex items-center gap-2 ${isFullscreen ? 'text-base' : 'text-lg'}`}>
              Projekt Timeline
              {isFullscreen && <span className="text-muted-foreground font-normal">- {project?.name}</span>}
              {!isCompleted && (
                <span className="text-[10px] font-normal text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                  <Move className="w-2.5 h-2.5 inline mr-1" />
                  Drag & Drop
                </span>
              )}
              {linkingFrom && (
                <span className="text-[10px] font-normal text-orange-600 bg-orange-100 px-1.5 py-0.5 rounded animate-pulse">
                  <Link2 className="w-2.5 h-2.5 inline mr-1" />
                  {linkingFrom.title}
                  <button onClick={() => setLinkingFrom(null)} className="ml-1">✕</button>
                </span>
              )}
            </CardTitle>
            <CardDescription className="text-xs">
              {new Date(project?.start_date).toLocaleDateString('de-CH')} - {new Date(project?.end_date).toLocaleDateString('de-CH')}
            </CardDescription>
          </div>
          
          {/* Controls Row */}
          <div className="flex items-center gap-2">
            {/* New Task Button */}
            {!isCompleted && onCreateTask && (
              <Button
                variant="default"
                size="sm"
                onClick={onCreateTask}
                className="h-7 text-xs gap-1"
                data-testid="timeline-new-task-btn"
              >
                <Plus className="w-3.5 h-3.5" />
                Neuer Task
              </Button>
            )}
            
            {/* Zoom Controls */}
            <div className="flex items-center gap-1">
            {/* Navigation arrows */}
            {zoomLevel !== 'all' && (
              <div className="flex items-center mr-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={scrollLeft}
                  disabled={!canScrollLeft}
                  className="h-7 w-7 p-0"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={scrollRight}
                  disabled={!canScrollRight}
                  className="h-7 w-7 p-0"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
            
            {/* View Mode Toggle - Start vs Today */}
            <div className="flex items-center gap-0.5 bg-muted rounded-md p-0.5 mr-2">
              <Button
                variant={viewMode === 'start' ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode('start')}
                className="h-6 text-[10px] px-2"
                title="Ansicht ab Projektstart"
              >
                Start
              </Button>
              <Button
                variant={viewMode === 'today' ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode('today')}
                className="h-6 text-[10px] px-2"
                title="Ansicht ab Heute (nur Zukunft)"
              >
                Heute
              </Button>
            </div>
            
            {/* Zoom Level Buttons */}
            <div className="flex items-center gap-0.5 bg-muted rounded-md p-0.5">
              {Object.entries(ZOOM_LEVELS).map(([key, config]) => (
                <Button
                  key={key}
                  variant={zoomLevel === key ? "default" : "ghost"}
                  size="sm"
                  onClick={() => handleZoomChange(key)}
                  className="h-6 text-[10px] px-2"
                >
                  {config.label}
                </Button>
              ))}
            </div>
            
            {/* Fullscreen Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="h-7 w-7 p-0 ml-2"
              title={isFullscreen ? "Vollbild beenden (ESC)" : "Vollbild"}
            >
              {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </Button>
            </div>
          </div>
        </div>
        
        {/* Legend */}
        <div className="flex gap-3 text-[10px] mt-2 flex-wrap">
          <div className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 bg-green-500 rounded-full" />
            <span>Erledigt</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 bg-primary rounded-full" />
            <span>Offen</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 bg-red-500 rounded-full" />
            <span>Überfällig</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 bg-orange-500 rounded-full" />
            <span>Kritisch</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-2 border-2 border-dashed border-violet-500 rounded-sm" />
            <span>Extern</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-0.5 bg-orange-500" />
            <span>Abhängigkeit</span>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className={isFullscreen ? "pt-0 flex-1 overflow-auto" : "pt-0"}>
        <div className={isFullscreen ? "space-y-2 h-full flex flex-col" : "space-y-4"}>
          {/* Progress Bar */}
          <div className="space-y-1 flex-shrink-0">
            <div className="flex items-center justify-between text-xs">
              <span className="font-medium">Fortschritt</span>
              <span className="font-bold">{calculateProgress()}%</span>
            </div>
            <div className={`relative bg-muted rounded overflow-hidden ${isFullscreen ? 'h-5' : 'h-6'}`}>
              <div className="absolute h-full bg-primary/80 transition-all rounded" style={{ width: `${calculateProgress()}%` }} />
              <div className="absolute inset-0 flex items-center justify-center text-[10px] font-medium">
                {tasks.filter(t => t.completed).length} / {tasks.length} Tasks
              </div>
            </div>
          </div>

          {/* Timeline Grid - key forces re-render on fullscreen change */}
          <div key={`timeline-${isFullscreen}-${renderKey}`} ref={timelineRef} className={`bg-muted/20 rounded-lg border border-border overflow-hidden relative ${isFullscreen ? 'flex-1' : ''}`}>
            {/* SVG Layer for Dependency Arrows */}
            <svg className="absolute inset-0 pointer-events-none z-20" style={{ width: '100%', height: '100%' }}>
              {sortedTasks.map((task, taskIndex) => 
                (task.depends_on || []).map(depId => {
                  const fromTask = taskMap[depId];
                  const toTask = task;
                  if (!fromTask || !toTask) return null;
                  
                  const fromIndex = sortedTasks.findIndex(t => t.id === depId);
                  if (fromIndex === -1) return null;
                  
                  const fromStartPos = fromTask.start_date ? getTimelinePosition(fromTask.start_date) : getTimelinePosition(fromTask.due_date);
                  const fromEndPos = getTimelinePosition(fromTask.due_date);
                  const toStartPos = getTimelinePosition(toTask.start_date || toTask.due_date);
                  const toEndPos = getTimelinePosition(toTask.due_date);
                  
                  // Skip if both tasks are completely outside view
                  if ((fromEndPos < -10 && toStartPos < -10) || (fromStartPos > 110 && toStartPos > 110)) {
                    return null;
                  }
                  
                  // DON'T draw arrow if:
                  // 1. Predecessor task extends beyond right edge (end > 100)
                  // 2. Dependent task extends beyond left edge (start < 0)
                  // 3. Predecessor task spans the entire visible area (would put point in middle of bar)
                  const fromTaskExtendsRight = fromEndPos > 100;
                  const fromTaskSpansEntireView = fromStartPos < 0 && fromEndPos > 100;
                  const toTaskExtendsLeft = toStartPos < 0;
                  const toTaskSpansEntireView = toStartPos < 0 && toEndPos > 100;
                  
                  // Skip drawing if the arrow would appear in the middle of a bar
                  if (fromTaskExtendsRight || fromTaskSpansEntireView || toTaskExtendsLeft || toTaskSpansEntireView) {
                    return null;
                  }
                  
                  // Use timelineWidth from state (not ref) to avoid reading refs during render
                  const currentTimelineWidth = timelineWidth;
                  
                  // Start point: END of predecessor task bar (point ON the bar, right edge)
                  const startX = LEFT_COLUMN_WIDTH + Math.max(0, Math.min(100, fromEndPos)) / 100 * currentTimelineWidth;
                  const startY = HEADER_HEIGHT + fromIndex * ROW_HEIGHT + ROW_HEIGHT / 2;
                  
                  // End point: START of dependent task bar (point ON the bar, left edge)
                  const endX = LEFT_COLUMN_WIDTH + Math.max(0, Math.min(100, toStartPos)) / 100 * currentTimelineWidth;
                  const endY = HEADER_HEIGHT + taskIndex * ROW_HEIGHT + ROW_HEIGHT / 2;
                  
                  let pathD;
                  if (Math.abs(startY - endY) < 5) {
                    // Same row - straight horizontal line
                    pathD = `M ${startX} ${startY} L ${endX} ${endY}`;
                  } else {
                    const outOffset = 12; // How far to go right from predecessor end
                    const turnRightX = startX + outOffset;
                    
                    // Check if dependent task starts "immediately after" predecessor
                    // (i.e., the line would cut through the dependent task bar)
                    const horizontalDistance = endX - startX;
                    const isImmediatelyAfter = horizontalDistance < 50; // Small gap = immediately after
                    
                    if (isImmediatelyAfter) {
                      // CENTERED path: right -> down to middle -> left -> down -> right to task
                      // This avoids cutting through the task bar
                      const midY = (startY + endY) / 2;
                      const approachOffset = 15;
                      const approachX = endX - approachOffset;
                      
                      pathD = `M ${startX} ${startY} 
                               L ${turnRightX} ${startY}
                               L ${turnRightX} ${midY}
                               L ${approachX} ${midY}
                               L ${approachX} ${endY}
                               L ${endX} ${endY}`;
                    } else {
                      // SIMPLE path: right -> down -> directly to task (no centering needed)
                      // The line goes straight to the task start without cutting through
                      pathD = `M ${startX} ${startY} 
                               L ${turnRightX} ${startY}
                               L ${turnRightX} ${endY}
                               L ${endX} ${endY}`;
                    }
                  }
                  
                  return (
                    <g key={`${depId}-${task.id}`}>
                      <path d={pathD} fill="none" stroke="#f97316" strokeWidth="2" strokeLinejoin="round" opacity="0.8" />
                      {/* Circle ON the left edge of dependent task bar */}
                      <circle cx={endX} cy={endY} r="4" fill="#f97316" />
                      {/* Circle ON the right edge of predecessor task bar */}
                      <circle cx={startX} cy={startY} r="4" fill="#f97316" />
                    </g>
                  );
                })
              )}
            </svg>

            {/* Header Row */}
            <div className="flex border-b border-border bg-muted/30">
              <div className="flex-none p-3 border-r border-border" style={{ width: LEFT_COLUMN_WIDTH }}>
                <div className="text-xs font-semibold text-muted-foreground">Tasks</div>
              </div>
              <div className="flex-1 relative overflow-hidden" style={{ height: HEADER_HEIGHT }}>
                {/* Timeline Markers */}
                {getTimelineMarkers().map((marker, index) => (
                  <div 
                    key={index} 
                    className={`absolute top-0 bottom-0 border-l ${marker.isWeekend ? 'border-orange-200 bg-orange-50/30' : 'border-border/50'}`}
                    style={{ left: `${marker.position}%` }}
                  >
                    <div className={`absolute top-2 left-1 text-[9px] font-medium whitespace-nowrap ${marker.isWeekend ? 'text-orange-400' : 'text-muted-foreground'}`}>
                      {marker.label}
                    </div>
                  </div>
                ))}
                
                {/* Project Start/End Markers */}
                {project?.start_date && (() => {
                  const startPos = getTimelinePosition(project.start_date);
                  if (startPos >= -5 && startPos <= 105) {
                    return (
                      <div className="absolute top-0 bottom-0 border-l-2 border-green-500 z-10" style={{ left: `${startPos}%` }}>
                        <div className="absolute top-8 left-1 text-[9px] font-bold text-green-600 bg-green-50 px-1 rounded">Start</div>
                      </div>
                    );
                  }
                  return null;
                })()}
                
                {project?.end_date && (() => {
                  const endPos = getTimelinePosition(project.end_date);
                  if (endPos >= -5 && endPos <= 105) {
                    return (
                      <div className="absolute top-0 bottom-0 border-l-2 border-red-500 z-10" style={{ left: `${endPos}%` }}>
                        <div className="absolute top-8 right-1 text-[9px] font-bold text-red-600 bg-red-50 px-1 rounded">Ende</div>
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Today Marker */}
                {(() => {
                  const today = new Date();
                  const position = getTimelinePosition(today.toISOString());
                  if (position >= -5 && position <= 105) {
                    return (
                      <div className="absolute top-0 bottom-0 border-l-2 border-blue-500 z-20" style={{ left: `${position}%` }}>
                        <div className="absolute bottom-1 left-1 text-[9px] font-bold text-blue-600 bg-blue-50 px-1 rounded">Heute</div>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            </div>

            {/* Task Rows */}
            <div className="relative">
              {sortedTasks.map((task, index) => {
                const duePosition = getTimelinePosition(task.due_date);
                const startPosition = task.start_date ? getTimelinePosition(task.start_date) : null;
                const today = new Date();
                const dueDate = new Date(task.due_date);
                const isOverdue = !task.completed && dueDate < today;
                const isDragging = draggingTask?.id === task.id;
                const isDropTarget = dragOverIndex === index && dragType === 'reorder';
                const isLinkingSource = linkingFrom?.id === task.id;
                const hasDependencies = task.depends_on && task.depends_on.length > 0;
                
                // Check if task is visible in current view
                const taskStart = startPosition !== null ? startPosition : duePosition;
                const taskEnd = duePosition;
                const isVisible = !(taskEnd < -10 && taskStart < -10) && !(taskEnd > 110 && taskStart > 110);
                
                return (
                  <div 
                    key={task.id}
                    ref={el => taskRowsRef.current[task.id] = el}
                    className={`flex border-b border-border/30 transition-all ${
                      isDragging ? 'opacity-50 bg-muted/50' : ''
                    } ${isDropTarget ? 'border-t-2 border-t-primary' : ''} ${
                      isLinkingSource ? 'bg-primary/10' : ''
                    } ${linkingFrom && !isLinkingSource ? 'cursor-pointer hover:bg-orange-50' : ''}`}
                    style={{ height: ROW_HEIGHT }}
                    onDragOver={(e) => handleReorderDragOver(e, index)}
                    onDrop={(e) => { if (dragType === 'reorder') handleReorderDrop(e, index); }}
                    onClick={() => { if (linkingFrom && !isLinkingSource) handleLinkTo(task); }}
                  >
                    {/* Left Column */}
                    <div 
                      className="flex-none p-2 bg-background/50 border-r border-border select-none"
                      style={{ width: LEFT_COLUMN_WIDTH }}
                    >
                      <div className="flex items-start gap-1">
                        {!isCompleted && onTaskReorder && (
                          <div 
                            className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground p-0.5 hover:bg-muted rounded"
                            draggable
                            onDragStart={(e) => handleReorderDragStart(e, task, index)}
                            onDragEnd={handleDragEnd}
                          >
                            <GripVertical className="w-3 h-3" />
                          </div>
                        )}
                        
                        {!isCompleted && onTaskDependencyUpdate && (
                          <button
                            className={`p-0.5 rounded ${isLinkingSource ? 'bg-orange-500 text-white' : 'text-muted-foreground hover:text-orange-500 hover:bg-orange-50'}`}
                            onClick={(e) => { e.stopPropagation(); handleStartLinking(task); }}
                          >
                            <Link2 className="w-3 h-3" />
                          </button>
                        )}
                        
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-xs truncate leading-tight flex items-center gap-1">
                            {task.title}
                            {task.is_external && (
                              <span className="text-[8px] bg-violet-100 text-violet-700 dark:bg-violet-900/50 dark:text-violet-300 px-1 rounded">
                                extern
                              </span>
                            )}
                          </div>
                          <div className="text-[9px] text-muted-foreground flex items-center gap-0.5">
                            <User className="w-2 h-2" />
                            <span className="truncate">{task.assigned_to_name}</span>
                          </div>
                          
                          {hasDependencies && (
                            <div className="flex flex-wrap gap-0.5 mt-0.5">
                              {task.depends_on.slice(0, 2).map(depId => {
                                const depTask = taskMap[depId];
                                if (!depTask) return null;
                                return (
                                  <span key={depId} className="inline-flex items-center text-[8px] bg-orange-100 text-orange-700 px-1 rounded">
                                    ←{depTask.title.substring(0, 6)}
                                    {!isCompleted && (
                                      <button onClick={(e) => { e.stopPropagation(); handleRemoveDependency(task, depId); }} className="ml-0.5 hover:text-red-500">✕</button>
                                    )}
                                  </span>
                                );
                              })}
                              {task.depends_on.length > 2 && (
                                <span className="text-[8px] text-orange-600">+{task.depends_on.length - 2}</span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Right Column - Timeline */}
                    <div 
                      className="flex-1 relative overflow-hidden"
                      onDragOver={(e) => { if (dragType === 'timeline') { handleTimelineDragOver(e, e.currentTarget, task.id); } }}
                      onDrop={(e) => { if (dragType === 'timeline') handleTimelineDrop(e, e.currentTarget); }}
                      onDragLeave={() => { if (dragType === 'timeline') setDragDatePreview(null); }}
                    >
                      {/* Grid lines */}
                      {getTimelineMarkers().map((marker, idx) => (
                        <div 
                          key={idx} 
                          className={`absolute top-0 bottom-0 border-l ${marker.isWeekend ? 'border-orange-100 bg-orange-50/20' : 'border-border/20'}`}
                          style={{ left: `${marker.position}%` }} 
                        />
                      ))}
                      
                      {/* Drag target vertical line - shown on ALL rows to create full-height line */}
                      {dragDatePreview && draggingTask && dragType === 'timeline' && (
                        <div 
                          className="absolute top-0 bottom-0 border-l-2 border-primary z-40 pointer-events-none"
                          style={{ left: `${getTimelinePosition(dragDatePreview.start.toISOString())}%` }}
                        />
                      )}
                      
                      {/* Drag ghost preview bar - only show on the row being dragged */}
                      {dragDatePreview && draggingTask && dragType === 'timeline' && draggingTask.id === task.id && (
                        <>
                          {/* Preview bar (ghost) for tasks with duration */}
                          {draggingTask.start_date && draggingTask.due_date && (
                            <div 
                              className="absolute h-6 top-1/2 -translate-y-1/2 bg-primary/20 border-2 border-primary rounded z-30 pointer-events-none"
                              style={{ 
                                left: `${getTimelinePosition(dragDatePreview.start.toISOString())}%`,
                                width: `${Math.max(2, getTimelinePosition(dragDatePreview.end.toISOString()) - getTimelinePosition(dragDatePreview.start.toISOString()))}%`
                              }}
                            />
                          )}
                          {/* For single-point tasks, show a circle indicator */}
                          {(!draggingTask.start_date || !draggingTask.due_date) && (
                            <div 
                              className="absolute w-6 h-6 top-1/2 -translate-y-1/2 -translate-x-1/2 bg-primary/30 border-2 border-primary rounded-full z-30 pointer-events-none"
                              style={{ left: `${getTimelinePosition(dragDatePreview.start.toISOString())}%` }}
                            />
                          )}
                        </>
                      )}
                      
                      {/* Project Start line (durchgehend wie Heute) */}
                      {project?.start_date && (() => {
                        const position = getTimelinePosition(project.start_date);
                        if (position >= -5 && position <= 105) {
                          return <div className="absolute top-0 bottom-0 border-l-2 border-green-400/50" style={{ left: `${position}%` }} />;
                        }
                        return null;
                      })()}
                      
                      {/* Project End line (durchgehend wie Heute) */}
                      {project?.end_date && (() => {
                        const position = getTimelinePosition(project.end_date);
                        if (position >= -5 && position <= 105) {
                          return <div className="absolute top-0 bottom-0 border-l-2 border-red-400/50" style={{ left: `${position}%` }} />;
                        }
                        return null;
                      })()}
                      
                      {/* Today line */}
                      {(() => {
                        const position = getTimelinePosition(new Date().toISOString());
                        if (position >= -5 && position <= 105) {
                          return <div className="absolute top-0 bottom-0 border-l-2 border-blue-400/40" style={{ left: `${position}%` }} />;
                        }
                        return null;
                      })()}
                      
                      {/* Task visualization */}
                      {isVisible && (
                        <div className="absolute inset-0 flex items-center px-1">
                          {startPosition !== null && Math.abs(startPosition - duePosition) > 1 ? (
                            // Task bar - properly clipped to visible area
                            (() => {
                              // Calculate visible portion of the task bar
                              const visibleStart = Math.max(0, Math.min(startPosition, duePosition));
                              const visibleEnd = Math.min(100, Math.max(startPosition, duePosition));
                              const visibleWidth = Math.max(2, visibleEnd - visibleStart);
                              const isCritical = criticalTaskSet.has(task.id);
                              const isExternal = task.is_external === true;
                              const barHeight = isFullscreen ? 'h-5' : 'h-7';
                              const hoverHeight = isFullscreen ? 'hover:h-6' : 'hover:h-8';
                              
                              // External tasks get special styling
                              const externalStyle = isExternal ? {
                                background: 'transparent',
                                border: '2px dashed rgb(139, 92, 246)', // violet-500
                                boxShadow: 'none'
                              } : {};
                              
                              return (
                                <div
                                  data-task-bar="true"
                                  className={`absolute ${barHeight} rounded transition-all ${
                                    !isExternal ? 'shadow-sm' : ''
                                  } ${
                                    !isCompleted && onTaskUpdate ? `cursor-grab active:cursor-grabbing ${hoverHeight}` : ''
                                  } ${
                                    isExternal 
                                      ? '' 
                                      : task.completed 
                                        ? 'bg-green-500' 
                                        : isOverdue 
                                          ? 'bg-red-500' 
                                          : isCritical 
                                            ? 'bg-orange-500 ring-2 ring-orange-300' 
                                            : 'bg-primary'
                                  }`}
                                  style={{ 
                                    left: `${visibleStart}%`,
                                    width: `${visibleWidth}%`,
                                    ...externalStyle
                                  }}
                                  draggable={!isCompleted && !!onTaskUpdate}
                                  onDragStart={(e) => handleTimelineDragStart(e, task)}
                                  onDragEnd={handleDragEnd}
                                  title={isExternal ? 'Externe Abhängigkeit - blockiert keine Ressourcen' : isCritical ? 'Kritischer Pfad - keine Verzögerung erlaubt!' : ''}
                                >
                                  <div className={`absolute inset-0 flex items-center px-1.5 ${isFullscreen ? 'text-[9px]' : 'text-[10px]'} ${isExternal ? 'text-violet-700 dark:text-violet-300' : 'text-white'} font-medium overflow-hidden`}>
                                    {task.completed && <CheckCircle2 className={`${isFullscreen ? 'w-2.5 h-2.5' : 'w-3 h-3'} mr-0.5 flex-shrink-0`} />}
                                    {isCritical && !task.completed && !isExternal && <AlertTriangle className={`${isFullscreen ? 'w-2.5 h-2.5' : 'w-3 h-3'} mr-0.5 flex-shrink-0`} />}
                                    <span className="truncate">{task.title}</span>
                                  </div>
                                </div>
                              );
                            })()
                          ) : (
                            // Single point
                            (() => {
                              const isCritical = criticalTaskSet.has(task.id);
                              const isExternal = task.is_external === true;
                              const pointSize = isFullscreen ? 'w-5 h-5' : 'w-6 h-6';
                              const iconSize = isFullscreen ? 'w-2.5 h-2.5' : 'w-3 h-3';
                              return (
                                <div
                                  data-task-bar="true"
                                  className="absolute flex items-center justify-center"
                                  style={{ left: `${Math.max(2, Math.min(98, duePosition))}%`, transform: 'translateX(-50%)' }}
                                  draggable={!isCompleted && !!onTaskUpdate}
                                  onDragStart={(e) => handleTimelineDragStart(e, task)}
                                  onDragEnd={handleDragEnd}
                                  title={isExternal ? 'Externe Abhängigkeit - blockiert keine Ressourcen' : isCritical ? 'Kritischer Pfad - keine Verzögerung erlaubt!' : ''}
                                >
                                  <div 
                                    className={`${pointSize} rounded-full border-2 flex items-center justify-center ${
                                      !isCompleted && onTaskUpdate ? 'cursor-grab hover:scale-110' : ''
                                    } ${
                                      isExternal 
                                        ? 'bg-transparent border-dashed border-violet-500' 
                                        : task.completed 
                                          ? 'bg-green-500 border-green-600' 
                                          : isOverdue 
                                            ? 'bg-red-500 border-red-600' 
                                            : isCritical 
                                              ? 'bg-orange-500 border-orange-400 ring-2 ring-orange-300' 
                                              : 'bg-primary border-primary'
                                    }`}
                                  >
                                    {task.completed && <CheckCircle2 className={`${iconSize} text-white`} />}
                                    {isCritical && !task.completed && !isExternal && <AlertTriangle className={`${iconSize} text-white`} />}
                                  </div>
                                </div>
                              );
                            })()
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              
              {tasks.length === 0 && (
                <div className="flex" style={{ height: ROW_HEIGHT }}>
                  <div className="flex-none p-3 bg-background/50 border-r border-border" style={{ width: LEFT_COLUMN_WIDTH }} />
                  <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
                    <Clock className="w-4 h-4 mr-2 opacity-50" />
                    Keine Tasks
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Timeline Scrollbar - BELOW the timeline grid - Always visible */}
          <div className="flex-shrink-0 mt-2">
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground w-20">Navigation:</span>
              <div 
                ref={scrollbarRef}
                className={`flex-1 h-4 bg-muted rounded-full relative select-none ${zoomLevel === 'all' ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                onMouseDown={(e) => {
                  if (zoomLevel === 'all') return; // Disabled in "all" mode
                  e.preventDefault();
                  const rect = e.currentTarget.getBoundingClientRect();
                  const updatePosition = (clientX) => {
                    const clickPosition = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
                    const totalDays = Math.ceil((fullBounds.end - fullBounds.start) / (24 * 60 * 60 * 1000));
                    const fullscreenMultiplier = isFullscreen ? 2 : 1;
                    const visibleDays = ZOOM_LEVELS[zoomLevel].daysVisible * fullscreenMultiplier;
                    const maxOffset = Math.max(0, totalDays - visibleDays);
                    const newOffset = Math.round(clickPosition * maxOffset);
                    setViewStartOffset(newOffset);
                  };
                  
                  // Initial click
                  updatePosition(e.clientX);
                  setIsDraggingScrollbar(true);
                  
                  // Mouse move handler
                  const handleMouseMove = (moveEvent) => {
                    updatePosition(moveEvent.clientX);
                  };
                  
                  // Mouse up handler
                  const handleMouseUp = () => {
                    setIsDraggingScrollbar(false);
                    document.removeEventListener('mousemove', handleMouseMove);
                    document.removeEventListener('mouseup', handleMouseUp);
                  };
                  
                  document.addEventListener('mousemove', handleMouseMove);
                  document.addEventListener('mouseup', handleMouseUp);
                }}
              >
                {/* Scrollbar track highlight on drag */}
                {isDraggingScrollbar && zoomLevel !== 'all' && (
                  <div className="absolute inset-0 bg-primary/10 rounded-full" />
                )}
                {/* Scrollbar thumb */}
                <div 
                  className={`absolute h-full rounded-full transition-colors ${
                    zoomLevel === 'all' 
                      ? 'bg-muted-foreground/30' 
                      : isDraggingScrollbar 
                        ? 'bg-primary scale-y-110' 
                        : 'bg-primary hover:bg-primary/80'
                  }`}
                  style={{
                    width: zoomLevel === 'all' ? '100%' : `${Math.max(10, Math.min(100, (() => {
                      const totalDays = Math.ceil((fullBounds.end - fullBounds.start) / (24 * 60 * 60 * 1000));
                      const fullscreenMultiplier = isFullscreen ? 2 : 1;
                      const visibleDays = ZOOM_LEVELS[zoomLevel].daysVisible * fullscreenMultiplier;
                      return (visibleDays / totalDays) * 100;
                    })()))}%`,
                    left: zoomLevel === 'all' ? '0%' : `${(() => {
                      const totalDays = Math.ceil((fullBounds.end - fullBounds.start) / (24 * 60 * 60 * 1000));
                      const fullscreenMultiplier = isFullscreen ? 2 : 1;
                      const visibleDays = ZOOM_LEVELS[zoomLevel].daysVisible * fullscreenMultiplier;
                      const maxOffset = Math.max(1, totalDays - visibleDays);
                      const thumbWidth = Math.max(10, Math.min(100, (visibleDays / totalDays) * 100));
                      const maxLeft = 100 - thumbWidth;
                      return Math.min(maxLeft, (viewStartOffset / maxOffset) * maxLeft);
                    })()}%`
                  }}
                />
              </div>
              <span className="text-xs text-muted-foreground w-24 text-right">
                {zoomLevel === 'all' 
                  ? 'Gesamtansicht'
                  : `${new Date(viewBounds.start).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit' })} - ${new Date(viewBounds.end).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit' })}`
                }
              </span>
            </div>
          </div>
        </div>
      </CardContent>
      
      {/* NEW: Date Preview Tooltip during drag */}
      {dragDatePreview && draggingTask && dragType === 'timeline' && (
        <div 
          className="fixed z-[100] pointer-events-none"
          style={{ 
            left: dragPosition.x + 15, 
            top: dragPosition.y - 50,
            transform: 'translateX(0)'
          }}
        >
          <div className="bg-primary text-primary-foreground px-3 py-2 rounded-lg shadow-xl border-2 border-primary-foreground/20">
            <div className="text-xs font-bold mb-1 flex items-center gap-1">
              <CalendarDays className="w-3 h-3" />
              {draggingTask.title}
            </div>
            {dragDatePreview.start ? (
              <div className="text-sm font-mono">
                <span className="text-primary-foreground/80 text-[10px]">Start:</span>{' '}
                <span className="font-bold">{dragDatePreview.start.toLocaleDateString('de-CH', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
                <br />
                <span className="text-primary-foreground/80 text-[10px]">Ende:</span>{' '}
                <span className="font-bold">{dragDatePreview.end.toLocaleDateString('de-CH', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
              </div>
            ) : (
              <div className="text-sm font-mono">
                <span className="text-primary-foreground/80 text-[10px]">Datum:</span>{' '}
                <span className="font-bold">{dragDatePreview.end.toLocaleDateString('de-CH', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
              </div>
            )}
          </div>
          {/* Arrow pointing down */}
          <div className="w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-primary ml-4" />
        </div>
      )}
    </Card>
    </>
  );
};

export default TimelineComponent;
